import React, { useCallback, useLayoutEffect, useRef } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { selectChatMessages, selectAccountConfig } from '@selectors'
import { SET_SHOW_SCROLL_INDICATOR } from '@redux/actions/types'

import ChatMessage from '@components/chat-message/chat-message.component'

import useStyles from './chat-messages.style'

import ResponsiveContainer from '@components/responsive-container/responsive-container.component'

const ChatMessages = (props) => {
  const classes = useStyles(props)

  const messagesEnd = useRef(null)

  const dispatch = useDispatch()

  const rawMessages = useSelector(selectChatMessages)
  const accountConfig = useSelector(selectAccountConfig)
  const messages = rawMessages?.slice().reverse() || []

  const userScrolledAwayRef = useRef(false)

  /*
   * DEV-5128: the ScrollUpButton pill (absolutely positioned below the
   * Header with `bottom: -37`) overlays the top ~30px of the scroll
   * content. It's supposed to dismiss when NewMessageIndicator enters
   * the viewport, but the InView handoff intermittently fails —
   * leaving the pill covering the topmost bubble. Belt-and-suspenders:
   * once the user has scrolled all the way up there is nothing further
   * above to scroll to, so dismiss the pill unconditionally.
   *
   * DEV-5197: also detect manual scroll-away so the auto-scroll-to-
   * bottom in useLayoutEffect stops fighting the user.
   */
  const handleScroll = (event) => {
    const t = event.target
    const distanceFromBottom = t.scrollHeight - t.scrollTop - t.clientHeight
    if (distanceFromBottom > 100) userScrolledAwayRef.current = true
    if (t.scrollTop <= 0) {
      dispatch({ type: SET_SHOW_SCROLL_INDICATOR, payload: false })
    }
  }

  const expiryBanner = React.useMemo(() => {
    if (accountConfig?.event_lookback?.enabled) {
      const { lookback_interval: eventInterval, lookback_window: eventWindow } = accountConfig.event_lookback
      const interval = Number(eventWindow) === 1 ? eventInterval.replace(/s$/, '') : `${eventWindow} ${eventInterval}`
      return (
        <div
          className={classes.messagesExpiryBanner}
          data-testid='expiry-banner'
        >
          <span
            className={`${classes.text} ${classes.footerText}`}
            data-testid='expiry-banner-text'
          >{`Showing events from the last ${interval}`}{' '}
          </span>
        </div>
      )
    }
    return null
  }, [accountConfig])

  const noMessages = React.useMemo(() => {
    if (messages.length === 0) {
      return (
        <div className={classes.noMessages}>
          <span className={`${classes.text} ${classes.noMessagesText}`}>
            No active events or messages available
          </span>
        </div>
      )
    }
    return null
  }, [accountConfig, messages.length])


  const getNextMessage = index => {
    const nextMessageIndex = index + 1

    const isOutOfBounds = nextMessageIndex > messages.length - 1

    if (isOutOfBounds) return null

    return messages[nextMessageIndex]
  }

  const getPreviousMessage = index => {
    const previousMessageIndex = index - 1

    const isOutOfBounds = previousMessageIndex < 0

    if (isOutOfBounds) return null

    return messages[previousMessageIndex]
  }

  const scrollToBottom = useCallback(() => {
    const container = messagesEnd.current?.parentElement
    if (!container) return
    container.scrollTop = container.scrollHeight
  }, [])

  /*
   * DEV-5197: async content (chat-message references, CPA media)
   * keeps growing the scroll container's scrollHeight after the
   * initial render without bumping messages.length. A single
   * scrollIntoView lands on the pre-resolve anchor position, leaving
   * the page partially scrolled.
   *
   * The scroll container has flex:1 and a fixed border box — only its
   * internal scrollHeight grows as async content settles, so a
   * ResizeObserver on the container never fires. MutationObserver on
   * the subtree DOES fire (childList + attribute changes during async
   * content load), which lets us re-pin to the bottom as long as the
   * user hasn't manually scrolled away. 5s cap covers slow image /
   * media decode without trapping later user interaction.
   */
  useLayoutEffect(() => {
    if (messages.length === 0) return undefined
    userScrolledAwayRef.current = false
    scrollToBottom()

    const container = messagesEnd.current?.parentElement
    if (!container) return undefined
    const mo = new MutationObserver(() => {
      if (!userScrolledAwayRef.current) scrollToBottom()
    })
    mo.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true
    })
    const stopId = setTimeout(() => mo.disconnect(), 5000)
    return () => {
      clearTimeout(stopId)
      mo.disconnect()
    }
  }, [messages.length, scrollToBottom])

  return (
    <div className={classes.messagesContainer}>
      <ResponsiveContainer
        className={classes.responsive}
        data-testid='messages-scroll-container'
        onScroll={handleScroll}
      >
        {noMessages}
        {messages && messages.map((message, index) => <ChatMessage
          key={`message-${index}`}
          index={index}
          message={message}
          nextMessage={getNextMessage(index)}
          previousMessage={getPreviousMessage(index)}
        />)}
        <div
          ref={messagesEnd}
          className={classes.messageEnd}
        />
      </ResponsiveContainer>
      {expiryBanner}
    </div>
  )
}

export default ChatMessages
